
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '@/lib/supabase';

interface ItineraryDay {
  day_number: number;
  title: string;
  location: string;
  description: string;
  activities: any[];
  accommodation: any;
  recommendations: any[];
}

interface Itinerary {
  id: string;
  title: string;
  destination: string;
  duration: number;
  description: string;
  cost_estimate: number;
  best_season: string;
  itinerary_days: ItineraryDay[];
}

interface ItineraryRequest {
  id: string;
  name: string;
  email: string;
  phone?: string;
  trip_type?: string;
  destination: string;
  travel_dates?: string;
  budget?: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export const exportItineraryToPDF = async (itineraryId: string) => {
  try {
    // Fetch itinerary data
    const { data: itinerary, error } = await supabase
      .from('itineraries')
      .select(`
        *,
        itinerary_days (*)
      `)
      .eq('id', itineraryId)
      .single();

    if (error) throw error;
    if (!itinerary) throw new Error('Itinerary not found');

    // Create PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(itinerary.title, margin, yPosition);
    yPosition += 15;

    // Destination and basic info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Destination: ${itinerary.destination}`, margin, yPosition);
    yPosition += 10;
    doc.text(`Duration: ${itinerary.duration} days`, margin, yPosition);
    yPosition += 10;
    doc.text(`Best Season: ${itinerary.best_season}`, margin, yPosition);
    yPosition += 10;
    if (itinerary.cost_estimate) {
      doc.text(`Estimated Cost: $${itinerary.cost_estimate}`, margin, yPosition);
      yPosition += 10;
    }

    // Description
    if (itinerary.description) {
      yPosition += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Description:', margin, yPosition);
      yPosition += 10;
      doc.setFont('helvetica', 'normal');
      const splitDescription = doc.splitTextToSize(itinerary.description, pageWidth - 2 * margin);
      doc.text(splitDescription, margin, yPosition);
      yPosition += splitDescription.length * 5 + 10;
    }

    // Days itinerary
    if (itinerary.itinerary_days && itinerary.itinerary_days.length > 0) {
      yPosition += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Daily Itinerary:', margin, yPosition);
      yPosition += 15;

      // Sort days by day_number
      const sortedDays = itinerary.itinerary_days.sort((a, b) => a.day_number - b.day_number);

      sortedDays.forEach((day) => {
        // Check if we need a new page
        if (yPosition > doc.internal.pageSize.getHeight() - 50) {
          doc.addPage();
          yPosition = margin;
        }

        // Day header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`Day ${day.day_number}: ${day.title}`, margin, yPosition);
        yPosition += 10;

        // Location
        if (day.location) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text(`Location: ${day.location}`, margin + 5, yPosition);
          yPosition += 8;
        }

        // Description
        if (day.description) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          const splitDesc = doc.splitTextToSize(day.description, pageWidth - 2 * margin - 10);
          doc.text(splitDesc, margin + 5, yPosition);
          yPosition += splitDesc.length * 5 + 5;
        }

        // Activities - properly handle the activities array
        const activities = Array.isArray(day.activities) ? day.activities : [];
        if (activities.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text('Activities:', margin + 5, yPosition);
          yPosition += 8;
          
          activities.forEach((activity: any) => {
            doc.setFont('helvetica', 'normal');
            const activityName = typeof activity === 'object' ? activity?.name || 'Activity' : String(activity);
            doc.text(`• ${activityName}`, margin + 10, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
        }

        // Accommodation - properly handle the accommodation object
        const accommodation = day.accommodation as any;
        if (accommodation && typeof accommodation === 'object' && accommodation.name) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text('Accommodation:', margin + 5, yPosition);
          yPosition += 8;
          doc.setFont('helvetica', 'normal');
          doc.text(`• ${accommodation.name}`, margin + 10, yPosition);
          if (accommodation.address) {
            yPosition += 6;
            doc.text(`  ${accommodation.address}`, margin + 10, yPosition);
          }
          yPosition += 10;
        }

        yPosition += 5;
      });
    }

    // Save the PDF
    doc.save(`${itinerary.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_itinerary.pdf`);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw error;
  }
};

export const exportRequestToPDF = (request: ItineraryRequest) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Itinerary Request', margin, yPosition);
    yPosition += 20;

    // Request details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${request.name}`, margin, yPosition);
    yPosition += 10;
    doc.text(`Email: ${request.email}`, margin, yPosition);
    yPosition += 10;
    
    if (request.phone) {
      doc.text(`Phone: ${request.phone}`, margin, yPosition);
      yPosition += 10;
    }
    
    doc.text(`Destination: ${request.destination}`, margin, yPosition);
    yPosition += 10;
    
    if (request.trip_type) {
      doc.text(`Trip Type: ${request.trip_type}`, margin, yPosition);
      yPosition += 10;
    }
    
    if (request.travel_dates) {
      doc.text(`Travel Dates: ${request.travel_dates}`, margin, yPosition);
      yPosition += 10;
    }
    
    if (request.budget) {
      doc.text(`Budget: ${request.budget}`, margin, yPosition);
      yPosition += 10;
    }
    
    doc.text(`Status: ${request.status}`, margin, yPosition);
    yPosition += 10;
    doc.text(`Created: ${new Date(request.created_at).toLocaleDateString()}`, margin, yPosition);
    yPosition += 20;

    // Message
    doc.setFont('helvetica', 'bold');
    doc.text('Message:', margin, yPosition);
    yPosition += 10;
    doc.setFont('helvetica', 'normal');
    const splitMessage = doc.splitTextToSize(request.message, pageWidth - 2 * margin);
    doc.text(splitMessage, margin, yPosition);

    // Save the PDF
    doc.save(`itinerary_request_${request.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
  } catch (error) {
    console.error('Error exporting request PDF:', error);
    throw error;
  }
};

export const exportBatchRequestsToPDF = (requests: ItineraryRequest[]) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Itinerary Requests Report', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
    doc.text(`Total Requests: ${requests.length}`, pageWidth - margin - 60, yPosition);
    yPosition += 20;

    // Create table data
    const tableData = requests.map(request => [
      request.name,
      request.email,
      request.destination,
      request.status,
      new Date(request.created_at).toLocaleDateString()
    ]);

    // Add table
    autoTable(doc, {
      head: [['Name', 'Email', 'Destination', 'Status', 'Date']],
      body: tableData,
      startY: yPosition,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 }
      }
    });

    // Save the PDF
    doc.save(`itinerary_requests_batch_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('Error exporting batch PDF:', error);
    throw error;
  }
};

// Helper: fetch image and convert to base64 data URL
const imageUrlToBase64 = async (url: string): Promise<{ dataUrl: string; width: number; height: number } | null> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ dataUrl: reader.result as string, width: img.width, height: img.height });
        img.onerror = () => resolve(null);
        img.src = reader.result as string;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Parse HTML content into ordered blocks of text and images
const parseHtmlToBlocks = (html: string): Array<{ type: 'text'; content: string } | { type: 'image'; src: string }> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks: Array<{ type: 'text'; content: string } | { type: 'image'; src: string }> = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        // Merge with previous text block if possible
        const last = blocks[blocks.length - 1];
        if (last && last.type === 'text') {
          last.content += ' ' + text;
        } else {
          blocks.push({ type: 'text', content: text });
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (el.tagName === 'IMG') {
        const src = el.getAttribute('src');
        if (src) blocks.push({ type: 'image', src });
        return;
      }
      // Block-level elements start a new text block
      const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'BR', 'HR', 'TABLE', 'TR'];
      if (blockTags.includes(el.tagName)) {
        const last = blocks[blocks.length - 1];
        if (last && last.type === 'text' && last.content) {
          // Force new block
          blocks.push({ type: 'text', content: '' });
        }
      }
      el.childNodes.forEach(walk);
      if (blockTags.includes(el.tagName)) {
        blocks.push({ type: 'text', content: '' }); // separator
      }
    }
  };

  doc.body.childNodes.forEach(walk);
  return blocks.filter(b => !(b.type === 'text' && !b.content));
};

interface BlogPostForPDF {
  id: string;
  title: string;
  slug: string;
  content: string | null;
  featured_image: string | null;
  excerpt: string | null;
  created_at: string;
  author?: { first_name?: string; last_name?: string } | null;
}

export const exportBlogPostToPDF = async (post: BlogPostForPDF, onProgress?: (msg: string) => void) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let y = margin;

    const checkPageBreak = (needed: number) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(post.title, contentWidth);
    checkPageBreak(titleLines.length * 8 + 10);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 8 + 5;

    // Author & date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const authorName = post.author ? `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() : 'Unknown';
    doc.text(`By ${authorName} · ${new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, y);
    y += 10;
    doc.setTextColor(0, 0, 0);

    // Excerpt
    if (post.excerpt) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(80, 80, 80);
      const excerptLines = doc.splitTextToSize(post.excerpt, contentWidth);
      checkPageBreak(excerptLines.length * 5 + 10);
      doc.text(excerptLines, margin, y);
      y += excerptLines.length * 5 + 10;
      doc.setTextColor(0, 0, 0);
    }

    // Featured image
    if (post.featured_image) {
      onProgress?.('Loading featured image...');
      const imgData = await imageUrlToBase64(post.featured_image);
      if (imgData) {
        const ratio = imgData.height / imgData.width;
        const imgWidth = Math.min(contentWidth, 170);
        const imgHeight = imgWidth * ratio;
        checkPageBreak(imgHeight + 10);
        doc.addImage(imgData.dataUrl, 'JPEG', margin, y, imgWidth, imgHeight);
        y += imgHeight + 10;
      }
    }

    // Content
    if (post.content) {
      onProgress?.('Processing content...');
      const blocks = parseHtmlToBlocks(post.content);
      
      for (const block of blocks) {
        if (block.type === 'text') {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(block.content, contentWidth);
          for (const line of lines) {
            checkPageBreak(6);
            doc.text(line, margin, y);
            y += 5;
          }
          y += 3; // paragraph spacing
        } else if (block.type === 'image') {
          onProgress?.('Loading image...');
          const imgData = await imageUrlToBase64(block.src);
          if (imgData) {
            const ratio = imgData.height / imgData.width;
            const imgWidth = Math.min(contentWidth, 170);
            const imgHeight = imgWidth * ratio;
            checkPageBreak(imgHeight + 10);
            doc.addImage(imgData.dataUrl, 'JPEG', margin, y, imgWidth, imgHeight);
            y += imgHeight + 10;
          }
        }
      }
    }

    doc.save(`${post.slug || post.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_blog.pdf`);
  } catch (error) {
    console.error('Error exporting blog post PDF:', error);
    throw error;
  }
};

export const exportAllBlogPostsToPDF = async (
  posts: BlogPostForPDF[],
  onProgress?: (current: number, total: number) => void
) => {
  for (let i = 0; i < posts.length; i++) {
    onProgress?.(i + 1, posts.length);
    await exportBlogPostToPDF(posts[i]);
    // Small delay between downloads so browser doesn't block them
    if (i < posts.length - 1) {
      await new Promise(r => setTimeout(r, 500));
    }
  }
};

export const exportPremiumItineraryToPDF = async (itinerary: any) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(itinerary.title, margin, yPosition);
    yPosition += 15;

    // Premium badge
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text('PREMIUM ITINERARY', margin, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 15;

    // Basic info
    doc.setFont('helvetica', 'normal');
    if (itinerary.destination) {
      doc.text(`Destination: ${itinerary.destination}`, margin, yPosition);
      yPosition += 10;
    }
    if (itinerary.duration) {
      doc.text(`Duration: ${itinerary.duration} days`, margin, yPosition);
      yPosition += 10;
    }
    if (itinerary.best_season) {
      doc.text(`Best Season: ${itinerary.best_season}`, margin, yPosition);
      yPosition += 10;
    }
    doc.text(`Price: ${itinerary.price} ${itinerary.currency}`, margin, yPosition);
    yPosition += 15;

    // Description
    if (itinerary.description) {
      doc.setFont('helvetica', 'bold');
      doc.text('Description:', margin, yPosition);
      yPosition += 10;
      doc.setFont('helvetica', 'normal');
      const splitDescription = doc.splitTextToSize(itinerary.description, pageWidth - 2 * margin);
      doc.text(splitDescription, margin, yPosition);
      yPosition += splitDescription.length * 5 + 10;
    }

    // Save the PDF
    doc.save(`${itinerary.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_premium.pdf`);
  } catch (error) {
    console.error('Error exporting premium itinerary PDF:', error);
    throw error;
  }
};
